import React, { useState, useEffect } from 'react';
import { BeakerIcon } from '@heroicons/react/24/outline';
import { vaccinationApi, CreateVaccinationScheduleDto } from '../../api/vaccinations';
import { getBirds } from '../../api/birds';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';
import AmountInput from '../ui/AmountInput';

interface Bird {
  id: string;
  bandId: string;
  species?: { commonName: string };
}

interface AddVaccinationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateVaccinationScheduleDto) => Promise<void>;
  birdId?: string;
  birds?: Bird[];
}

const AddVaccinationModal: React.FC<AddVaccinationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  birdId,
  birds = [],
}) => {
  const [formData, setFormData] = useState<CreateVaccinationScheduleDto>({
    birdId: birdId || '',
    vaccineName: '',
    vaccineType: 'ROUTINE',
    scheduledDate: '',
    veterinarian: '',
    clinicName: '',
    dosage: '',
    administrationRoute: 'INTRAMUSCULAR',
    notes: '',
    cost: 0,
  });
  const [availableBirds, setAvailableBirds] = useState<Bird[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Use provided birds or load them if not provided
      if (Array.isArray(birds) && birds.length > 0) {
        setAvailableBirds(birds);
      } else {
        loadBirds();
      }
      
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setFormData(prev => ({
        ...prev,
        scheduledDate: tomorrow.toISOString().slice(0, 16),
        birdId: birdId || prev.birdId,
      }));
    }
  }, [isOpen, birdId, birds]);

  const loadBirds = async () => {
    try {
      setLoading(true);
      const response = await getBirds();
      setAvailableBirds(response);
    } catch (error) {
      console.error('Error loading birds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'cost' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.birdId || !formData.vaccineName || !formData.scheduledDate) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit(formData);
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error scheduling vaccination:', error);
      alert('Failed to schedule vaccination. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      birdId: birdId || '',
      vaccineName: '',
      vaccineType: 'ROUTINE',
      scheduledDate: '',
      veterinarian: '',
      clinicName: '',
      dosage: '',
      administrationRoute: 'INTRAMUSCULAR',
      notes: '',
      cost: 0,
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <BrandModal open={isOpen} onClose={handleClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader
        title={
          <span className="inline-flex items-center">
            <BeakerIcon className="h-6 w-6 text-green-500 mr-2" />
            Schedule Vaccination
          </span>
        }
        onClose={handleClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">Bird *</label>              <select
                name="birdId"
                value={formData.birdId}
                onChange={handleInputChange}
                required
                disabled={!!birdId || loading}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="empty-option-1753461297320" value="">Select a bird...</option>
                <option key="ALL_BIRDS-1753461297320" value="ALL_BIRDS" className="font-medium text-cyan-600">
                  🔵 All Birds / Flock
                </option>
                {Array.isArray(availableBirds) && availableBirds.map((bird) => (
                  <option key={bird.id} value={bird.id}>
                    {bird.bandId} {bird.species?.commonName ? `(${bird.species.commonName})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Vaccine Name *</label>
              <input
                type="text"
                name="vaccineName"
                value={formData.vaccineName}
                onChange={handleInputChange}
                required
                placeholder="e.g., Newcastle Disease Vaccine"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Vaccine Type *</label>
              <select
                name="vaccineType"
                value={formData.vaccineType}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="ROUTINE-1753461297320" value="ROUTINE">Routine</option>
                <option key="EMERGENCY-1753461297320" value="EMERGENCY">Emergency</option>
                <option key="BOOSTER-1753461297320" value="BOOSTER">Booster</option>
                <option key="SEASONAL-1753461297320" value="SEASONAL">Seasonal</option>
                <option key="PREVENTIVE-1753461297320" value="PREVENTIVE">Preventive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Scheduled Date & Time *</label>
              <input
                type="datetime-local"
                name="scheduledDate"
                value={formData.scheduledDate}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Dosage</label>
              <input
                type="text"
                name="dosage"
                value={formData.dosage}
                onChange={handleInputChange}
                placeholder="e.g., 0.5ml"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Administration Route</label>
              <select
                name="administrationRoute"
                value={formData.administrationRoute}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="INTRAMUSCULAR-1753461297320" value="INTRAMUSCULAR">Intramuscular</option>
                <option key="SUBCUTANEOUS-1753461297320" value="SUBCUTANEOUS">Subcutaneous</option>
                <option key="INTRAVENOUS-1753461297321" value="INTRAVENOUS">Intravenous</option>
                <option key="ORAL-1753461297321" value="ORAL">Oral</option>
                <option key="NASAL-1753461297321" value="NASAL">Nasal</option>
                <option key="OCULAR-1753461297321" value="OCULAR">Ocular</option>
                <option key="TOPICAL-1753461297321" value="TOPICAL">Topical</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Veterinarian</label>
              <input
                type="text"
                name="veterinarian"
                value={formData.veterinarian}
                onChange={handleInputChange}
                placeholder="Dr. Smith"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Clinic Name</label>
              <input
                type="text"
                name="clinicName"
                value={formData.clinicName}
                onChange={handleInputChange}
                placeholder="Avian Veterinary Clinic"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700">Cost ($)</label>
              <AmountInput
                name="cost"
                value={formData.cost}
                onChange={(v) => setFormData(prev => ({ ...prev, cost: v ?? 0 }))}
                min={0}
                inputClassName="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Additional notes about the vaccination..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {submitting ? 'Scheduling...' : 'Schedule Vaccination'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default AddVaccinationModal; 

